import * as bootstrap from 'bootstrap';

const jsKit = (() => {
  const defaults = {
    modalID: '#popup_container',
    debugMode: false,
  };

  const currentAbortController = null;

  const rwdMedia = {};
  const delegatedEvents = {};
  let delegatedEventOrder = 0;

  ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'].forEach((bp) => rwdMedia[bp] = () => document.querySelector(`.media-${bp}`).offsetParent !== null);

  const getCtoken = () => document.cookie.split('; ')
    .find((cookie) => cookie.startsWith('ctoken='))
    ?.split('=').slice(1).join('') || '';

  const appendCtoken = (data) => {
    const token = getCtoken();
    if (!token || !data || (!(data instanceof FormData) && !(data instanceof URLSearchParams))) {
      return;
    }

    const formNames = new Set([...data.keys()]
      .map((key) => key.match(/^([^[]+)\[/)?.[1])
      .filter(Boolean));
    formNames.forEach((formName) => {
      const tokenKey = `${formName}[ctoken]`;
      if (!data.has(tokenKey)) {
        data.set(tokenKey, token);
      }
    });
  };

  return {
    currentAbortController,

    defaults,

    rwdMedia,

    isIOS: () => /iPhone|iPad|iPod/i.test(navigator.userAgent),

    isAndroid: () => /Android/i.test(navigator.userAgent),

    isMobile: () => jsKit.isIOS() || jsKit.isAndroid(),

    getIndex: (nodeList, element) => Array.from(nodeList).indexOf(element),

    requestUrl: async (url, dataType = 'json', method = 'GET', data = null, options = {}) => {
      const headers = {};
      let body;
      let signal;

      if (method === 'POST' && options.appendCtoken !== false) {
        appendCtoken(data);
      }

      if (options.abortPrevious) {
        if (jsKit.currentAbortController) {
          jsKit.currentAbortController.abort();
        }

        jsKit.currentAbortController = new AbortController();
        signal = jsKit.currentAbortController.signal;
      } else if (options.signal) {
        signal = options.signal;
      }

      if (!(data instanceof FormData)) {
        if (dataType === 'json') {
          headers['Content-Type'] = 'application/json';
          body = JSON.stringify(data);
        } else if (dataType === 'text') {
          headers['Content-Type'] = 'text/plain';
          body = data;
        } else if (dataType === 'urlencoded' && data) {
          headers['Content-Type'] = 'application/x-www-form-urlencoded';
          body = new URLSearchParams(data).toString();
        }
      } else {
        body = data;
      }

      try {
        const response = await fetch(url, {
          method,
          ...(method === 'POST' && data ? { body } : {}),
          headers,
          ...(signal ? { signal } : {}),
        });

        return dataType === 'json' || dataType === 'urlencoded' ? await response.json() : await response.text();
      } catch (error) {
        console.log('Wystąpił błąd: ', error);
      }
    },

    fetchUrl: async (url, cb, dataType = 'json', method = 'GET', data = null, options = {}) => {
      const result = await jsKit.requestUrl(url, dataType, method, data, options);
      if (typeof cb === 'function' && typeof result !== 'undefined') {
        cb(result);
      }
    },

    getDataParts: (element, dataItem, separator = ', ') => {
      if (!element || !element.dataset || !element.dataset[dataItem]) {
        return [];
      }

      return element.dataset[dataItem].split(separator);
    },

    setLoading: (element, isLoading = true, options = {}) => {
      if (!element) return;

      const { disabled = false, className = 'loading' } = options;
      element.classList.toggle(className, isLoading);

      if (disabled && 'disabled' in element) {
        element.disabled = isLoading;
      }
    },

    setHtml: (target, html) => {
      const element = typeof target === 'string' ? document.querySelector(target) : target;
      if (element) {
        element.innerHTML = html;
      }

      return element;
    },

    initTooltipsIn: (parentElem) => {
      if (!parentElem) return;
      jsKit.initTooltip(parentElem.querySelectorAll('[data-bs-toggle="tooltip"]'));
    },

    disposeTooltip: (element) => {
      const tooltip = bootstrap.Tooltip.getInstance(element);
      tooltip?.dispose();
    },

    validateForm: (form) => {
      const invalidFields = Array.from(form.querySelectorAll('[data-js-validate][data-js-invalid-text]'))
        .map((input) => jsKit.validateInput(input))
        .filter((isValid) => isValid === false);

      return invalidFields.length === 0;
    },

    handleValidatedResponse: async (data, options = {}) => {
      if (!data) return;

      const {
        onValid = null,
        onInvalid = null,
        errorType = 'error',
        showError = true,
      } = options;

      if (data.validated) {
        if (typeof onValid === 'function') {
          await onValid(data);
        }
        return;
      }

      if (typeof onInvalid === 'function') {
        await onInvalid(data);
        return;
      }

      if (showError && data.error_message) {
        jsKit.showNotificationsPanel(data.error_message, errorType);
      }
    },

    closeModal: () => {
      const domLargeModal = document.querySelector(defaults.modalID);
      const isOpen = domLargeModal.classList.contains('show');
      const bsModal = bootstrap.Modal.getOrCreateInstance(domLargeModal);
      if (isOpen) {
        bsModal.hide();
      }
    },

    emptyModal: () => {
      const domLargeModal = document.querySelector(defaults.modalID);
      domLargeModal.querySelector('.modal-content').innerHTML = '';
    },

    showModal: (modalID) => {
      if (!document.querySelector(modalID)) {
        return;
      }

      const modal = bootstrap.Modal.getOrCreateInstance(document.querySelector(modalID));
      modal.show();
    },

    openModal: (html, customs = {}) => {
      if (defaults.debugMode) {
        console.log('openModal()', customs);
      }

      const defaultOptions = {
        size: 'modal-lg',
        config: { backdrop: 'true' },
        shownCB: false,
        hiddenCB: false,
      };
      const options = { ...defaultOptions, ...customs};
      const domLargeModal = document.querySelector(defaults.modalID);
      const isOpen = domLargeModal.classList.contains('show');
      const bsModal = bootstrap.Modal.getOrCreateInstance(domLargeModal);

      if (isOpen) {
        bsModal.hide();
      }

      domLargeModal.addEventListener('shown.bs.modal', () => {
        jsKit.autofocus(domLargeModal);
        if (options.shownCB) {
          options.shownCB();
        }
      }, { once: true });

      domLargeModal.addEventListener('hidden.bs.modal', () => {
        jsKit.emptyModal();
        if (options.hiddenCB) {
          options.hiddenCB();
        }
      }, { once: true });

      //domLargeModal.querySelector('.modal-dialog').classList.add(options.size);
      jsKit.emptyModal();
      domLargeModal.innerHTML = html;
      bsModal.show();
    },

    modalData: async (e) => {
      if (defaults.debugMode) {
        console.log('modalData()');
      }

      const { target } = e;
      const isBtn = target.classList.contains('btn');
      if (isBtn) {
        target.classList.add('loading');
      }

      const url = target.dataset.jsModalData;
      if (!url) return;

      const getCB = (html) => {
        jsKit.openModal(html);
        if (isBtn) {
          target.classList.remove('loading');
        }
      };
      await jsKit.fetchUrl(url, (html) => getCB(html));
    },

    containerData: async (e) => {
      if (defaults.debugMode) {
        console.log('containerData()');
      }

      const { target } = e;
      const isBtn = target.classList.contains('btn');
      if (isBtn) {
        target.classList.add('loading');
      }

      const [url, containerId] = target.dataset.jsContainerData.split(', ');

      const getCB = (html) => {
        document.querySelector(`#${containerId}`).innerHTML = html;
        if (isBtn) {
          target.classList.remove('loading');
        }
      };
      await jsKit.fetchUrl(url, html => getCB(html));
    },

    autofocus: (parentElem) => {
      const autofocusInput = parentElem.querySelector('[autofocus]');
      if (autofocusInput && !jsKit.isMobile()) {
        autofocusInput.focus();

        if (autofocusInput.tagName === 'INPUT' && autofocusInput.type === 'text') {
          const length = autofocusInput.value.length;
          autofocusInput.setSelectionRange(length, length);
        }
      }
    },

    initTooltip: (tooltipTriggerList) => {
      [...tooltipTriggerList].forEach((tooltipTriggerEl) => bootstrap.Tooltip.getOrCreateInstance(tooltipTriggerEl));
    },

    initPopover: (popoverTriggerList) => {
      [...popoverTriggerList].forEach((popoverTriggerEl) => bootstrap.Popover.getOrCreateInstance(popoverTriggerEl));
    },

    toKebabCase: (str) => str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase(),

    runIfDataSetExists: (dataItem, fn) => {
      const selector = `[data-${jsKit.toKebabCase(dataItem)}]`;
      const el = document.querySelector(selector);

      if (el && typeof fn === 'function') {
        fn();
      }
    },

    attachEventByDataSet: (dataItem, fn, eventName = 'click') => {
      if (eventName === 'DOMContentLoaded') {
        jsKit.runIfDataSetExists(dataItem, fn);
        return;
      }

      delegatedEvents[eventName] = delegatedEvents[eventName] || {
        handlers: {},
        isBound: false,
      };

      delegatedEvents[eventName].handlers[dataItem] = delegatedEvents[eventName].handlers[dataItem] || [];
      delegatedEvents[eventName].handlers[dataItem].push({
        dataItem,
        fn,
        order: delegatedEventOrder,
      });
      delegatedEventOrder += 1;

      if (delegatedEvents[eventName].isBound) return;
      delegatedEvents[eventName].isBound = true;

      const useCapture = eventName === 'blur' || eventName === 'focus';

      document.addEventListener(eventName, (e) => {
          const eventConfig = delegatedEvents[eventName];
          const handlers = eventConfig ? eventConfig.handlers : {};
          const matches = [];
          const matchedDataItems = new Set();
          const target = e.target;
          let node = target && target.nodeType === Node.ELEMENT_NODE ? target : target && target.parentElement;

          while (node && node !== document) {
            if (node.dataset) {
              Object.keys(node.dataset).forEach((item) => {
                if (!handlers[item] || matchedDataItems.has(item)) return;

                matchedDataItems.add(item);
                handlers[item].forEach((handlerConfig) => {
                  matches.push({
                    ...handlerConfig,
                    el: node,
                  });
                });
              });
            }

            node = node.parentElement;
          }

          matches
            .sort((a, b) => a.order - b.order)
            .forEach(({ dataItem: item, fn: handler, el }) => {
              if (typeof handler !== 'function') return;

              e.delegateTarget = el;
              e.delegateDataItem = item;

              if (defaults.debugMode) {
                console.log('attachEventByDataSet match', item, eventName, el);
              }

              handler(e);
            });
        }, useCapture);
    },

    loadFlashMessages: async (section) => {
      if (section) {
        const url = `/main/loadFlashMessages/${section}`;

        const getCB = async (data) => {
          jsKit.showNotificationsPanel(data.messages);
        };

        await jsKit.fetchUrl(url, getCB);
      }
    },

    showErrors: (data, context = 'body') => {
      if (defaults.debugMode) {
        console.log(`showErrors(${data})`);
      }

      if (typeof data !== 'undefined') {

        const generalError = typeof data.error_message !== 'undefined' && data.error_message;
        jsKit.showNotificationsPanel(generalError);

        const errors = typeof data.error !== 'undefined' && data.error;

        if (!errors) {
          return;
        }

        for (const key of Object.keys(errors)) {
          const contextElem = document.querySelector(context);
          const target = contextElem.querySelector(`[data-valid="${key}"]`);
          const errorClass = 'is-invalid';

          if (target) {
            if (errors[key] !== '') {
              if (defaults.debugMode) {
                console.log(`[data-valid="${key}"]`);
              }

              if (!target.classList.contains(errorClass)) {
                target.classList.add(errorClass);
                const feedback = target.nextElementSibling;
                if (feedback && feedback.classList.contains('invalid-feedback')) {
                  feedback.innerText = errors[key];
                }
                target.addEventListener('focus', () => target.classList.remove(errorClass));
              }
            } else {
              target.classList.remove(errorClass);
            }
          }
        }
      }
    },

    showNotificationsPanel: (msg, type = 'error') => {
      const toastLive = document.getElementById('liveToast');
      const toast = new bootstrap.Toast(toastLive);
      const notificationsPanel = document.querySelector('[data-notifications-panel]');
      const notificationsPanelMsg = notificationsPanel.querySelector('[data-notifications-panel-msg]');

      if (Array.isArray(msg) && msg.every((item) => typeof item === 'object' && item.content)) {
        msg.forEach((item) => {
          const paragraph = document.createElement('p'); // Tworzenie elementu <p>
          paragraph.textContent = item.content;
          notificationsPanelMsg.appendChild(paragraph);
        });
      } else if (typeof msg === 'string') {
        notificationsPanelMsg.innerHTML = msg;
      }

      toast.show();
    },

    validateInput: (input) => {
      const pattern = input.dataset.jsValidate;
      const invalidText = input.dataset.jsInvalidText;
      const invalidFeedbackElem = input.nextElementSibling;
      if (defaults.debugMode) {
        console.log(`inputValid(${input})`);
      }

      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(input.value)) {
        input.classList.remove('is-invalid');
      } else {
        input.classList.add('is-invalid');
        invalidFeedbackElem.innerText = invalidText;
      }

      return (regex.test(input.value));
    },
  };
})();

export default jsKit;
